import React, { useEffect, useState } from 'react';
import {
  DEFAULT_LOGO_STYLE,
  MAX_POSITION_OFFSET_PX,
  getDropShadowCss,
  getLogoPreviewMetrics,
  getPositionControlValue,
  normalizeLogoStyleConfig,
} from '../utils/logoOverlayConfig';
import { prepareLogoPreviewAsset } from '../utils/logoImageProcessing';
import { getLogoPreviewOptions } from '../utils/logoPreviewSamples';

const PREVIEW_FRAMES = [
  { id: 'landscape', label: 'Landscape', width: 320, height: 208 },
  { id: 'portrait', label: 'Portrait', width: 208, height: 320 },
];

function StyleLogoEditor({ value, onChange, logoSrc, previewImageSrc, portraitPreviewImageSrc, styleName }) {
  const logoStyle = normalizeLogoStyleConfig(value);
  const [logoAspectRatio, setLogoAspectRatio] = useState(1);
  const [processedLogoSrc, setProcessedLogoSrc] = useState('');
  const [selectedPreviewId, setSelectedPreviewId] = useState('');
  const { options: previewOptions, recommendedOptionId } = getLogoPreviewOptions(styleName, previewImageSrc, portraitPreviewImageSrc);
  const activePreviewId = previewOptions.some((option) => option.id === selectedPreviewId)
    ? selectedPreviewId
    : recommendedOptionId;
  const activePreviewOption = previewOptions.find((option) => option.id === activePreviewId) || previewOptions[0];
  const activePreviewSrc = activePreviewOption?.src || '';
  const activePortraitPreviewSrc = activePreviewOption?.portraitSrc || activePreviewSrc;

  useEffect(() => {
    if (!logoSrc) {
      setLogoAspectRatio(1);
      setProcessedLogoSrc('');
      return undefined;
    }

    let isCancelled = false;

    prepareLogoPreviewAsset(logoSrc)
      .then(({ src, aspectRatio }) => {
        if (!isCancelled) {
          setProcessedLogoSrc(src || logoSrc);
          setLogoAspectRatio(aspectRatio > 0 ? aspectRatio : 1);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setProcessedLogoSrc(logoSrc);
          setLogoAspectRatio(1);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [logoSrc]);

  const updateLogoStyle = (patch) => {
    onChange(normalizeLogoStyleConfig({ ...logoStyle, ...patch }));
  };

  return (
    <section className="style-logo-editor">
      <div className="style-logo-editor__header">
        <div>
          <h3>Style Icon Overlay</h3>
          <p>Adjust the icon size, pixel position, and shadow. Pixel offsets are measured from the top-left of the final rendered map.</p>
        </div>
        <button
          type="button"
          className="style-logo-editor__reset"
          onClick={() => onChange(normalizeLogoStyleConfig(DEFAULT_LOGO_STYLE))}
        >
          Reset to defaults
        </button>
      </div>

      <div className="style-logo-editor__grid">
        <div className="style-logo-editor__controls">
          <div className="style-logo-editor__control">
            <label htmlFor="logo-style-size">Size</label>
            <div className="style-logo-editor__range-row">
              <input
                id="logo-style-size"
                className="style-logo-editor__range-input"
                type="range"
                min="8"
                max="42"
                step="0.5"
                value={logoStyle.sizePercent}
                onChange={(e) => updateLogoStyle({ sizePercent: Number(e.target.value) })}
              />
              <span>{logoStyle.sizePercent.toFixed(1)}%</span>
            </div>
          </div>

          <div className="style-logo-editor__control">
            <label htmlFor="logo-style-x">Horizontal Position</label>
            <div className="style-logo-editor__range-row">
              <input
                id="logo-style-x"
                className="style-logo-editor__range-input"
                type="range"
                min="0"
                max={MAX_POSITION_OFFSET_PX}
                step="1"
                value={getPositionControlValue(logoStyle, 'x')}
                onChange={(e) => updateLogoStyle({ xOffsetPx: Number(e.target.value) })}
              />
              <div className="style-logo-editor__value-input">
                <input
                  id="logo-style-x-value"
                  type="number"
                  min="0"
                  max={MAX_POSITION_OFFSET_PX}
                  step="1"
                  value={getPositionControlValue(logoStyle, 'x')}
                  onChange={(e) => updateLogoStyle({ xOffsetPx: Number(e.target.value) })}
                  aria-label="Horizontal position in pixels"
                />
                <span>px</span>
              </div>
            </div>
          </div>

          <div className="style-logo-editor__control">
            <label htmlFor="logo-style-y">Vertical Position</label>
            <div className="style-logo-editor__range-row">
              <input
                id="logo-style-y"
                className="style-logo-editor__range-input"
                type="range"
                min="0"
                max={MAX_POSITION_OFFSET_PX}
                step="1"
                value={getPositionControlValue(logoStyle, 'y')}
                onChange={(e) => updateLogoStyle({ yOffsetPx: Number(e.target.value) })}
              />
              <div className="style-logo-editor__value-input">
                <input
                  id="logo-style-y-value"
                  type="number"
                  min="0"
                  max={MAX_POSITION_OFFSET_PX}
                  step="1"
                  value={getPositionControlValue(logoStyle, 'y')}
                  onChange={(e) => updateLogoStyle({ yOffsetPx: Number(e.target.value) })}
                  aria-label="Vertical position in pixels"
                />
                <span>px</span>
              </div>
            </div>
          </div>

          <div className="style-logo-editor__control">
            <label htmlFor="logo-style-shadow">Shadow</label>
            <div className="style-logo-editor__range-row">
              <input
                id="logo-style-shadow"
                className="style-logo-editor__range-input"
                type="range"
                min="0"
                max="200"
                step="1"
                value={logoStyle.shadowAmount}
                onChange={(e) => updateLogoStyle({ shadowAmount: Number(e.target.value) })}
              />
              <span>{logoStyle.shadowAmount}</span>
            </div>
          </div>
        </div>

        <div className="style-logo-editor__preview-column">
          <div className="style-logo-editor__preview-header">
            <div>
              <h4>Live Preview</h4>
              <p>Defaults to the example map shown on /create. Curated alternatives are available for extra contrast checks.</p>
            </div>
            <div className="style-logo-editor__preview-source">
              <label htmlFor="logo-preview-sample">Preview Base</label>
              <select
                id="logo-preview-sample"
                value={activePreviewId}
                onChange={(e) => setSelectedPreviewId(e.target.value)}
              >
                {previewOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="style-logo-editor__preview-grid">
            {PREVIEW_FRAMES.map((frame) => {
              const metrics = getLogoPreviewMetrics(logoStyle, frame.width, frame.height, logoAspectRatio);
              const shadowReferenceSize = Math.max(metrics.badgeWidth, metrics.badgeHeight);
              const frameSrc = frame.id === 'portrait' ? activePortraitPreviewSrc : activePreviewSrc;

              return (
                <div key={frame.id} className="style-logo-editor__preview-card">
                  <div className="style-logo-editor__preview-meta">
                    <span>{frame.label}</span>
                    <small>{frame.width}×{frame.height}</small>
                  </div>
                  <div
                    className="style-logo-editor__preview-frame"
                    style={{ width: frame.width, height: frame.height }}
                  >
                    {frameSrc ? (
                      <img
                        className="style-logo-editor__preview-image"
                        src={frameSrc}
                        alt={`${frame.label} preview base: ${activePreviewOption?.label || 'selected sample'}`}
                      />
                    ) : (
                      <div className="style-logo-editor__preview-empty">
                        <span>Preview base</span>
                      </div>
                    )}

                    {logoStyle.enabled && (
                      <div
                        className="style-logo-editor__badge"
                        style={{
                          left: metrics.left,
                          top: metrics.top,
                          width: metrics.badgeWidth,
                          height: metrics.badgeHeight,
                        }}
                      >
                        <div
                          className="style-logo-editor__badge-content"
                          style={{
                            filter: getDropShadowCss(logoStyle.shadowAmount, shadowReferenceSize),
                          }}
                        >
                          {processedLogoSrc ? (
                            <img
                              className="style-logo-editor__logo"
                              src={processedLogoSrc}
                              alt="Style icon overlay preview"
                              style={{
                                left: metrics.logoLeft,
                                top: metrics.logoTop,
                                width: metrics.logoWidth,
                                height: metrics.logoHeight,
                                opacity: logoStyle.iconOpacity,
                              }}
                            />
                          ) : (
                            <div
                              className="style-logo-editor__logo-placeholder"
                              style={{
                                left: metrics.logoLeft,
                                top: metrics.logoTop,
                                width: metrics.logoWidth,
                                height: metrics.logoHeight,
                                opacity: logoStyle.iconOpacity,
                              }}
                            >
                              Logo
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default StyleLogoEditor;
